import { throttle as F } from "lodash-es";
import { hexToFractionalRgb as p, lineStringToLines as R, pointsAndPointsToLines as m, subSetArray as L } from "@allmaps/stdlib";
import { black as _, white as P, pink as g, blue as U } from "@allmaps/tailwind";
import G from "./TriangulatedWarpedMap.js";
import { WarpedMapEvent as D, WarpedMapEventType as O } from "../shared/events.js";
import { applyTransform as x } from "../shared/matrix.js";
import { createBuffer as a } from "../shared/webgl2.js";
import { getTilesAtOtherScaleFactors as w, tileKey as y } from "../shared/tiles.js";
const I = 200, v = {
  leading: !0,
  trailing: !0
}, S = 1, j = 1, C = 6, X = [...p(_), 1], b = 0, B = [...p(P), 1], M = 16, V = [...p(_), 1], N = 1, W = [...p(P), 1], z = 5, Y = 1;
function te(A, e, r, n) {
  return (l, T, i) => new Z(
    l,
    T,
    A,
    e,
    n,
    r,
    i
  );
}
class Z extends G {
  gl;
  mapsProgram;
  linesProgram;
  pointsProgram;
  mapsVao = null;
  linesVao = null;
  pointsVao = null;
  lineLayers = [];
  pointLayers = [];
  // Consider to store cachedTilesByTileKey as a quadtree for faster lookups
  cachedTilesByTileKey = /* @__PURE__ */ new Map();
  cachedTilesByTileUrl = /* @__PURE__ */ new Map();
  cachedTilesForTexture = [];
  previousCachedTilesForTexture = [];
  textureWidth = 0;
  textureHeight = 0;
  textureDepth = 0;
  opacity = S;
  saturation = j;
  renderOptions = {};
  cachedTilesTextureArray = null;
  cachedTilesResourcePositionsAndDimensionsTexture = null;
  cachedTilesScaleFactorsTexture = null;
  projectedGeoToClipTransform;
  throttledUpdateTextures;
  /**
   * Creates an instance of WebGL2WarpedMap.
   *
   * @constructor
   * @param {string} mapId - ID of the map
   * @param {GeoreferencedMap} georeferencedMap - Georeferenced map used to construct the WarpedMap
   * @param {WebGL2RenderingContext} gl - WebGL rendering context
   * @param {WebGLProgram} mapsProgram - WebGL program
   * @param {Partial<WarpedMapOptions>} options - WarpedMapOptions
   */
  constructor(e, r, n, l, T, i, o) {
    super(e, r, o), this.gl = n, this.initializeWebGL(l, T, i), this.throttledUpdateTextures = F(
      this.updateTextures.bind(this),
      I,
      v
    );
  }
  initializeWebGL(e, r, n) {
    this.mapsProgram = e, this.linesProgram = r, this.pointsProgram = n, this.mapsVao = this.gl.createVertexArray(), this.linesVao = this.gl.createVertexArray(), this.pointsVao = this.gl.createVertexArray(), this.cachedTilesTextureArray = this.gl.createTexture(), this.cachedTilesScaleFactorsTexture = this.gl.createTexture(), this.cachedTilesResourcePositionsAndDimensionsTexture = this.gl.createTexture();
  }
  /**
   * Update the vertex buffers of this warped map
   *
   * @param {Transform} projectedGeoToClipTransform - Transform from projected geo coordinates to webgl2 coordinates in the [-1, 1] range. Equivalent to OpenLayers' projectionTransform.
   */
  updateVertexBuffers(e) {
    this.projectedGeoToClipTransform = e, this.updateVertexBuffersMaps();
  }
  /**
   * Clear textures for this map
   */
  clearTextures() {
    this.throttledUpdateTextures();
  }
  /**
   * Add cached tile to the textures of this map and update textures
   *
   * @param {CachedTile} cachedTile
   */
  addCachedTileAndUpdateTextures(e) {
    this.cachedTilesByTileKey.set(e.tileKey, e), this.cachedTilesByTileUrl.set(e.tileUrl, e), this.throttledUpdateTextures();
  }
  /**
   * Remove cached tile from the textures of this map and update textures
   *
   * @param {string} tileUrl
   */
  removeCachedTileAndUpdateTextures(e) {
    const r = this.cachedTilesByTileUrl.get(e);
    r && this.cachedTilesByTileKey.delete(r.tileKey), this.cachedTilesByTileUrl.delete(e), this.throttledUpdateTextures();
  }
  cancelThrottledFunctions() {
    this.throttledUpdateTextures.cancel();
  }
  destroy() {
    this.gl.deleteVertexArray(this.mapsVao), this.gl.deleteVertexArray(this.linesVao), this.gl.deleteVertexArray(this.pointsVao), this.gl.deleteTexture(this.cachedTilesTextureArray), this.gl.deleteTexture(this.cachedTilesScaleFactorsTexture), this.gl.deleteTexture(this.cachedTilesResourcePositionsAndDimensionsTexture), this.cancelThrottledFunctions(), super.destroy();
  }
  setLineLayers() {
    this.lineLayers = [
      {
        projectedGeoLines: R(this.projectedGeoLongerMask),
        projectedGeoPreviousLines: R(
          this.projectedGeoPreviousLongerMask
        ),
        viewportSize: 8,
        color: [...p(g), 1]
      },
      {
        projectedGeoLines: m(
          this.projectedGeoPoints,
          this.projectedGeoTransformedResourcePoints
        ),
        projectedGeoPreviousLines: m(
          this.projectedGeoPoints,
          this.projectedGeoPreviousTransformedResourcePoints
        ),
        color: [...p(_), 1],
        borderColor: [...p(P), 1]
      }
    ];
  }
  setPointLayers() {
    this.pointLayers = [
      {
        projectedGeoPoints: this.projectedGeoPoints,
        color: [...p(U), 1]
      },
      {
        projectedGeoPoints: this.projectedGeoTransformedResourcePoints,
        projectedGeoPreviousPoints: this.projectedGeoPreviousTransformedResourcePoints,
        color: [...p(g), 1]
      }
    ];
  }
  updateVertexBuffersMaps() {
    if (!this.mapsVao || !this.projectedGeoToClipTransform)
      return;
    const e = this.gl, r = this.mapsProgram;
    e.bindVertexArray(this.mapsVao), a(
      e,
      r,
      new Float32Array(this.resourceTrianglePoints.flat()),
      2,
      "a_resourceTrianglePoint"
    );
    const n = this.projectedGeoPreviousTrianglePoints.map(
      (i) => x(this.projectedGeoToClipTransform, i)
    );
    a(
      e,
      r,
      new Float32Array(n.flat()),
      2,
      "a_clipPreviousTrianglePoint"
    );
    const l = this.projectedGeoTrianglePoints.map(
      (i) => x(this.projectedGeoToClipTransform, i)
    );
    a(
      e,
      r,
      new Float32Array(l.flat()),
      2,
      "a_clipTrianglePoint"
    ), a(
      e,
      r,
      new Float32Array(this.previousTrianglePointsDistortion),
      1,
      "a_previousTrianglePointDistortion"
    ), a(
      e,
      r,
      new Float32Array(this.trianglePointsDistortion),
      1,
      "a_trianglePointDistortion"
    );
    const T = new Float32Array(
      this.resourceTrianglePoints.length
    ).map((i, o) => o);
    a(
      e,
      r,
      T,
      1,
      "a_trianglePointIndex"
    );
  }
  updateVertexBuffersLines() {
    if (!this.linesVao)
      return;
    const e = this.gl, r = this.linesProgram;
    e.bindVertexArray(this.linesVao), this.setLineLayers();
    const n = this.lineLayers.reduce(
      (t, s) => t.concat(s.projectedGeoLines),
      []
    ).map((t) => [
      t[0],
      t[0],
      t[0],
      t[1],
      t[1],
      t[1]
    ]).flat();
    a(
      e,
      r,
      new Float32Array(n.flat()),
      2,
      "a_projectedGeoPoint"
    );
    const l = this.lineLayers.reduce(
      (t, s) => t.concat(s.projectedGeoLines),
      []
    ).map((t) => [
      t[1],
      t[1],
      t[1],
      t[0],
      t[0],
      t[0]
    ]).flat();
    a(
      e,
      r,
      new Float32Array(l.flat()),
      2,
      "a_projectedGeoOtherPoint"
    );
    const T = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoPreviousLines || s.projectedGeoLines
      ),
      []
    ).map((t) => [
      t[0],
      t[0],
      t[0],
      t[1],
      t[1],
      t[1]
    ]).flat();
    a(
      e,
      r,
      new Float32Array(T.flat()),
      2,
      "a_projectedGeoPreviousPoint"
    );
    const i = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoPreviousLines || s.projectedGeoLines
      ),
      []
    ).map((t) => [
      t[1],
      t[1],
      t[1],
      t[0],
      t[0],
      t[0]
    ]).flat();
    a(
      e,
      r,
      new Float32Array(i.flat()),
      2,
      "a_projectedGeoPreviousOtherPoint"
    );
    const o = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoLines.flatMap((E) => [
          0,
          0,
          1,
          0,
          0,
          1
        ])
      ),
      []
    );
    a(
      e,
      r,
      new Float32Array(o),
      1,
      "a_isOtherPoint"
    );
    const d = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoLines.flatMap((E) => [
          1,
          -1,
          1,
          1,
          -1,
          1
        ])
      ),
      []
    );
    a(
      e,
      r,
      new Float32Array(d),
      1,
      "a_normalSign"
    );
    const h = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoLines.flatMap(
          (E) => Array(6).fill(
            Object.prototype.hasOwnProperty.call(s, "viewportSize") ? s.viewportSize : C
          )
        )
      ),
      []
    );
    a(
      e,
      r,
      new Float32Array(h),
      1,
      "a_viewportSize"
    );
    const c = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoLines.flatMap(
          (E) => Array(6).fill(
            Object.prototype.hasOwnProperty.call(s, "color") ? s.color : X
          )
        )
      ),
      []
    );
    a(e, r, new Float32Array(c.flat()), 4, "a_color");
    const u = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoLines.flatMap(
          (E) => Array(6).fill(
            s.viewportBorderSize || b
          )
        )
      ),
      []
    );
    a(
      e,
      r,
      new Float32Array(u),
      1,
      "a_viewportBorderSize"
    );
    const f = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoLines.flatMap(
          (E) => Array(6).fill(
            Object.prototype.hasOwnProperty.call(s, "borderColor") ? s.borderColor : B
          )
        )
      ),
      []
    );
    a(
      e,
      r,
      new Float32Array(f.flat()),
      4,
      "a_borderColor"
    );
  }
  updateVertexBuffersPoints() {
    if (!this.pointsVao)
      return;
    const e = this.gl, r = this.pointsProgram;
    e.bindVertexArray(this.pointsVao), this.setPointLayers();
    const n = this.pointLayers.reduce(
      (h, c) => h.concat(c.projectedGeoPoints),
      []
    );
    a(
      e,
      r,
      new Float32Array(n.flat()),
      2,
      "a_projectedGeoPoint"
    );
    const l = this.pointLayers.reduce(
      (h, c) => h.concat(
        c.projectedGeoPreviousPoints || c.projectedGeoPoints
      ),
      []
    );
    a(
      e,
      r,
      new Float32Array(l.flat()),
      2,
      "a_projectedGeoPreviousPoint"
    );
    const T = this.pointLayers.reduce(
      (h, c) => h.concat(
        c.projectedGeoPoints.map(
          (u) => Object.prototype.hasOwnProperty.call(c, "viewportSize") ? c.viewportSize : M
        )
      ),
      []
    );
    a(
      e,
      r,
      new Float32Array(T),
      1,
      "a_viewportSize"
    );
    const i = this.pointLayers.reduce(
      (h, c) => h.concat(
        c.projectedGeoPoints.map(
          (u) => Object.prototype.hasOwnProperty.call(c, "color") ? c.color : V
        )
      ),
      []
    );
    a(e, r, new Float32Array(i.flat()), 4, "a_color");
    const o = this.pointLayers.reduce(
      (h, c) => h.concat(
        c.projectedGeoPoints.map(
          (u) => c.viewportBorderSize || N
        )
      ),
      []
    );
    a(
      e,
      r,
      new Float32Array(o),
      1,
      "a_viewportBorderSize"
    );
    const d = this.pointLayers.reduce(
      (h, c) => h.concat(
        c.projectedGeoPoints.map(
          (u) => Object.prototype.hasOwnProperty.call(c, "borderColor") ? c.borderColor : W
        )
      ),
      []
    );
    a(
      e,
      r,
      new Float32Array(d.flat()),
      4,
      "a_borderColor"
    );
  }
  async updateTextures() {
    const e = this.gl;
    if (this.updateCachedTilesForTextures(), this.cachedTilesForTexture.length != 0 && L(
      this.previousCachedTilesForTexture.map(
        (o) => o.tileUrl
      ),
      this.cachedTilesForTexture.map((o) => o.tileUrl)
    ))
      return;
    const r = Math.max(
      ...this.parsedImage.tileZoomLevels.map((o) => o.width)
    ), n = Math.max(
      ...this.parsedImage.tileZoomLevels.map((o) => o.height)
    ), l = this.cachedTilesForTexture.length;
    e.pixelStorei(e.UNPACK_ALIGNMENT, 4), e.bindTexture(e.TEXTURE_2D_ARRAY, this.cachedTilesTextureArray), e.texImage3D(
      e.TEXTURE_2D_ARRAY,
      0,
      e.RGBA,
      r,
      n,
      l,
      0,
      e.RGBA,
      e.UNSIGNED_BYTE,
      null
    );
    for (let o = 0; o < this.cachedTilesForTexture.length; o++) {
      const d = this.cachedTilesForTexture[o].data;
      e.texSubImage3D(
        e.TEXTURE_2D_ARRAY,
        0,
        0,
        0,
        o,
        d.width,
        d.height,
        1,
        e.RGBA,
        e.UNSIGNED_BYTE,
        d
      );
    }
    e.texParameteri(e.TEXTURE_2D_ARRAY, e.TEXTURE_MIN_FILTER, e.LINEAR), e.texParameteri(e.TEXTURE_2D_ARRAY, e.TEXTURE_MAG_FILTER, e.LINEAR), e.texParameteri(e.TEXTURE_2D_ARRAY, e.TEXTURE_WRAP_S, e.CLAMP_TO_EDGE), e.texParameteri(e.TEXTURE_2D_ARRAY, e.TEXTURE_WRAP_T, e.CLAMP_TO_EDGE);
    const T = this.cachedTilesForTexture.map((o) => {
      if (o && o.imageRequest && o.imageRequest.region)
        return [
          o.imageRequest.region.x,
          o.imageRequest.region.y,
          o.imageRequest.region.width,
          o.imageRequest.region.height
        ];
    });
    e.bindTexture(
      e.TEXTURE_2D,
      this.cachedTilesResourcePositionsAndDimensionsTexture
    ), e.texImage2D(
      e.TEXTURE_2D,
      0,
      e.R32I,
      1,
      this.cachedTilesForTexture.length * 4,
      0,
      e.RED_INTEGER,
      e.INT,
      new Int32Array(T.flat())
    ), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_MIN_FILTER, e.NEAREST), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_MAG_FILTER, e.NEAREST), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_WRAP_S, e.CLAMP_TO_EDGE), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_WRAP_T, e.CLAMP_TO_EDGE);
    const i = this.cachedTilesForTexture.map(
      (o) => o.tile.tileZoomLevel.scaleFactor
    );
    e.bindTexture(e.TEXTURE_2D, this.cachedTilesScaleFactorsTexture), e.texImage2D(
      e.TEXTURE_2D,
      0,
      e.R32I,
      1,
      this.cachedTilesForTexture.length,
      0,
      e.RED_INTEGER,
      e.INT,
      new Int32Array(i)
    ), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_MIN_FILTER, e.NEAREST), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_MAG_FILTER, e.NEAREST), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_WRAP_S, e.CLAMP_TO_EDGE), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_WRAP_T, e.CLAMP_TO_EDGE), this.dispatchEvent(new D(O.TEXTURESUPDATED));
  }
  updateCachedTilesForTextures() {
    const e = [], r = [], n = [];
    for (const i of this.currentFetchableTiles) {
      const o = this.cachedTilesByTileUrl.get(i.tileUrl);
      if (o)
        e.push(o);
      else
        for (const d of this.getCachedTilesAtOtherScaleFactors(
          i.tile
        ))
          r.push(d);
    }
    for (const i of this.currentOverviewFetchableTiles) {
      const o = this.cachedTilesByTileUrl.get(i.tileUrl);
      if (o) {
        const d = this.currentTileZoomLevel ? this.currentTileZoomLevel.rows * this.currentTileZoomLevel.columns : void 0;
        (e.length == 0 || d && e.length < d) && n.push(o);
      }
    }
    let l = [
      ...e,
      ...r,
      ...n
    ];
    const T = /* @__PURE__ */ new Map();
    l.forEach(
      (i) => T.set(i.tileUrl, i)
    ), l = [...T.values()], this.previousCachedTilesForTexture = this.cachedTilesForTexture, this.cachedTilesForTexture = l;
  }
  getCachedTilesAtOtherScaleFactors(e) {
    if (this.cachedTilesByTileUrl.size == 0)
      return [];
    const r = [];
    for (e of w(
      e,
      this.parsedImage,
      this.currentBestScaleFactor,
      Y,
      z,
      this.tileInCachedTiles.bind(this)
      // Only consider tiles in cache,
    )) {
      const n = this.tileToCachedTile(e);
      if (n)
        r.push(n);
      else
        throw new Error("Tile supposed to be in cache isn't.");
    }
    return r;
  }
  // Lookup by tileKey (zoomlevel, row, column) instead of tileUrl
  // Because computing the tileUrl for every tile is expensive
  tileToCachedTile(e) {
    return this.cachedTilesByTileKey.get(y(e));
  }
  tileInCachedTiles(e) {
    return this.cachedTilesByTileKey.has(y(e));
  }
}
export {
  te as createWebGL2WarpedMapFactory,
  Z as default
};
//# sourceMappingURL=WebGL2WarpedMap.js.map
